// L'onglet Émargements.
//
// La mise en page est celle d'origine — bandeau, quatre indicateurs, tableau
// par session avec ses six colonnes. **Ce sont les chiffres qui ont changé.**
//
// Ils étaient écrits en dur : `{ label: 'Feuilles générées', value: 142 }`.
// 142, 127, 94 %, 15 — quatre constantes qui n'ont jamais rien lu. Elles
// affichaient la même chose le premier jour et l'auraient affichée dans dix
// ans. Le tableau, lui, lisait une collection de compteurs de démonstration,
// le bouton d'envoi n'envoyait rien, et le téléchargement appelait une
// fonction nommée `downloadAsFakePdf`.
//
// Tout vient maintenant de `/emargement/feuilles`, qui part des FORMATIONS et
// non des séances : une formation passée dont la feuille n'a jamais été créée
// doit apparaître, c'est précisément une preuve manquante.
//
// **Et une feuille signée sur papier se dépose.** Le lien de signature est le
// bon chemin, mais il ne couvre pas tout : un lien périmé, un formateur sans
// téléphone, une salle sans réseau, un client qui a fait signer son propre
// imprimé. Ces formations ont eu lieu, et leur preuve n'avait aucun endroit
// où aller — la ligne restait rouge pour toujours. Le scan ou la photo se
// range sur la formation, et vaut preuve de réalisation.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PenTool, ClipboardCheck, CheckCircle2, XCircle, Send, Download, Link2,
  Loader2, AlertTriangle, Upload, Paperclip, Trash2, X, Award, Printer,
} from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';
import { messageErreur } from '../lib/erreur';
import PanneauAttestations from '../components/PanneauAttestations';

const dateFr = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR');
  } catch { return iso; }
};

// Un instant complet — il porte son décalage, donc le navigateur le convertit
// juste, ici comme pour qui le lit depuis un autre pays.
const dateHeureFr = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

export default function Emargements() {
  const [feuilles, setFeuilles] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [tout, setTout] = useState(true);
  const [occupe, setOccupe] = useState(null);
  const [explication, setExplication] = useState(false);
  // Les formations preparees ou personne n'est inscrit. Une feuille sans
  // apprenant ne peut rien prouver : ni signature, ni attestation, et aucun
  // questionnaire ne peut partir. Six des sept de la base sont deja passees.
  const [sansApprenant, setSansApprenant] = useState(false);
  // La formation dont on regarde les pieces deposees, et celle qui attend un
  // fichier : le champ de fichier est unique, c'est son porteur qui change.
  const [pieces, setPieces] = useState(null);
  // La formation dont on regarde les attestations. Elles vivent ici parce que
  // c'est l'assiduité qui les autorise, et c'est cet écran qui la montre.
  const [attestations, setAttestations] = useState(null);
  // La formation dont on prépare l'envoi : à qui, et quoi. L'écran envoyait en
  // aveugle à l'adresse de la fiche du formateur, et se faisait refuser sans
  // recours quand aucune fiche ne portait ce nom.
  const [envoi, setEnvoi] = useState(null);
  const champFichier = React.useRef(null);
  // Les trois panneaux s'inserent **en tete de page**, et on les ouvre depuis
  // une ligne du tableau, tout en bas. On cliquait, la page ne bougeait pas,
  // et le panneau s'affichait la ou on ne regardait pas : « il ne se passe
  // rien », dit trois fois, pour un panneau qui s'ouvrait a chaque fois.
  const zonePanneaux = React.useRef(null);
  const cible = React.useRef(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get('/emargement/feuilles', { params: { tout } });
      setFeuilles(data);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || 'Impossible de charger les feuilles.',
        variant: 'destructive',
      });
    } finally { setChargement(false); }
  }, [tout]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    if (!pieces && !attestations && !envoi) return;
    zonePanneaux.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [pieces, attestations, envoi]);

  const vides = useMemo(
    () => feuilles.filter((f) => f.preparee && (f.attendues_apprenants ?? 1) === 0),
    [feuilles],
  );
  const videsPassees = useMemo(() => vides.filter((f) => f.passee), [vides]);
  // Celles qui demarrent aujourd'hui ou demain : c'est le seul moment ou l'on
  // peut encore inscrire quelqu'un ou annuler. Trois semaines apres, on ne
  // peut plus que constater — et c'est a ce moment-la que le positionnement
  // se fait refuser « Aucun apprenant inscrit », toutes les heures, en
  // silence.
  const videsImminentes = useMemo(() => {
    const demain = new Date();
    demain.setDate(demain.getDate() + 1);
    const borne = demain.toISOString().slice(0, 10);
    return vides.filter((f) => !f.passee && (f.debut || '') <= borne);
  }, [vides]);

  // Les quatre indicateurs, calculés sur ce que le tableau montre.
  const stats = useMemo(() => {
    const attendues = feuilles.reduce((s, f) => s + (f.attendues || 0), 0);
    const signees = feuilles.reduce((s, f) => s + (f.signees || 0), 0);
    return [
      { label: 'Feuilles générées', value: feuilles.filter((f) => f.preparee).length, color: '#4F46E5' },
      { label: 'Signées', value: feuilles.filter((f) => f.complete).length, color: '#14B8A6' },
      {
        label: 'Taux de signature',
        // Sans aucune signature attendue, un « 0 % » se lirait comme un échec.
        value: attendues ? `${Math.round((signees / attendues) * 100)}%` : '—',
        color: '#3B82F6',
      },
      { label: 'En attente', value: Math.max(0, attendues - signees), color: '#F59E0B' },
    ];
  }, [feuilles]);

  // Deposer : on ouvre le champ de fichier, et c'est son changement qui
  // envoie. Un champ par ligne ferait autant de champs caches que de
  // formations, pour un geste qui ne se fait qu'une fois.
  const demanderFichier = (f) => {
    cible.current = f.session_id;
    if (champFichier.current) {
      champFichier.current.value = '';
      champFichier.current.click();
    }
  };

  const deposer = async (evt) => {
    const fichier = evt.target.files && evt.target.files[0];
    const sessionId = cible.current;
    if (!fichier || !sessionId) return;
    setOccupe(`${sessionId}:deposer`);
    try {
      const corps = new FormData();
      corps.append('file', fichier);
      corps.append('genre', 'emargement');
      // L'en-tete multipart : l'instance axios annonce du JSON par defaut, et
      // le formulaire partait donc annonce comme tel — FastAPI le refusait en
      // 422. C'est ce que posent les trois autres depots du logiciel.
      const { data } = await api.post(`/sessions/${sessionId}/documents`, corps, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast({
        title: 'Feuille déposée',
        description: `${data.filename} — ${Math.max(1, Math.round((data.size || 0) / 1024))} Ko.`,
      });
      await charger();
    } catch (err) {
      toast({
        title: 'Dépôt impossible',
        description: messageErreur(err, "Le fichier n'a pas été accepté."),
        variant: 'destructive',
      });
    } finally {
      setOccupe(null);
      cible.current = null;
    }
  };

  const agir = async (f, action) => {
    setOccupe(`${f.session_id}:${action}`);
    try {
      if (action === 'preparer') {
        const { data } = await api.post(`/sessions/${f.session_id}/emargement/preparer`);
        toast({ title: 'Feuille préparée', description: `${data.seances} séance(s), ${data.lignes_creees} ligne(s).` });
        await charger();
      } else if (action === 'envoyer') {
        // Ouvrir la fenêtre plutôt qu'envoyer : c'est elle qui demande
        // l'adresse et ce qu'on envoie.
        setEnvoi(f);
        setOccupe(null);
        return;
      } else if (action === 'lien') {
        const { data } = await api.post(`/sessions/${f.session_id}/emargement/lien`, {});
        const url = data.url.startsWith('http') ? data.url : `${window.location.origin}${data.url}`;
        try {
          await navigator.clipboard.writeText(url);
          toast({
            title: 'Lien copié',
            description: "Envoyez-le au formateur."
              + (data.expire_le ? ` Valable jusqu'au ${dateFr(data.expire_le)}.` : ''),
          });
        } catch {
          // Le presse-papiers est refusé hors HTTPS : on montre le lien.
          window.prompt('Lien à envoyer au formateur :', url);
        }
      } else if (action === 'pdf') {
        const res = await api.get(`/sessions/${f.session_id}/emargement.pdf`, { responseType: 'blob' });
        const url = URL.createObjectURL(res.data);
        window.open(url, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
    } catch (err) {
      toast({
        title: 'Impossible',
        description: err?.response?.data?.detail || 'Action refusée.',
        variant: 'destructive',
      });
    } finally { setOccupe(null); }
  };

  return (
    <div className="space-y-5">
      <input
        ref={champFichier}
        type="file"
        accept="application/pdf,image/png,image/jpeg,image/webp,image/heic,image/heif"
        onChange={deposer}
        className="hidden"
        data-testid="emarg-champ-fichier"
      />
      {/* La zone n'existe que lorsqu'un panneau l'habite : un conteneur vide
          garderait l'ecart de `space-y-5` sous le bandeau. */}
      {(pieces || attestations || envoi) && (
        <div ref={zonePanneaux} className="space-y-5">
          {pieces && (
            <PanneauPieces
              feuille={pieces}
              onFermer={() => setPieces(null)}
              onChange={charger}
            />
          )}
          {attestations && (
            <PanneauAttestations
              feuille={attestations}
              onFermer={() => setAttestations(null)}
              onChange={charger}
            />
          )}
          {envoi && (
            <PanneauEnvoi
              feuille={envoi}
              onFermer={() => setEnvoi(null)}
              onEnvoye={charger}
            />
          )}
        </div>
      )}
      {/* Bandeau */}
      <div className="rounded-2xl p-5 flex items-center gap-4"
           style={{ background: 'linear-gradient(120deg, #EEF2FF 0%, #E6FBF7 100%)' }}>
        <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shrink-0">
          <PenTool className="w-6 h-6 text-[#4F46E5]" />
        </div>
        <div className="flex-1">
          <h3 className="text-[15.5px] font-semibold text-[#1A1B2E]"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Signature électronique conforme
          </h3>
          <p className="text-[13px] text-[#4A4B63]">
            Les émargements signés numériquement sont juridiquement valides et acceptés par les OPCO.
          </p>
        </div>
        <button
          onClick={() => setExplication((v) => !v)}
          data-testid="emarg-en-savoir-plus"
          className="h-9 px-4 rounded-lg bg-white border border-[#C7D2FE] text-[13px] font-semibold text-[#4F46E5] hover:bg-[#EEF2FF] transition-colors shrink-0"
        >
          {explication ? 'Masquer' : 'En savoir plus'}
        </button>
      </div>

      {explication && (
        <div className="soft-card p-5 text-[13px] text-[#4A4B63] space-y-2">
          <div className="font-semibold text-[#1A1B2E]">Ce que la plateforme enregistre</div>
          <p>
            Chaque signature est recueillie par un lien propre à la formation, transmis au
            formateur. Sont conservés le tracé manuscrit, le nom du signataire, la séance
            concernée avec sa date et ses horaires, et l'horodatage de la signature.
          </p>
          <p>
            Une signature déjà recueillie ne peut plus être remplacée, ni la présence retirée
            à qui a signé, ni la séance supprimée : une preuve de présence ne s'efface pas.
          </p>
          <p className="text-[#8E8FA5]">
            La valeur juridique d'une signature électronique et son acceptation par un
            financeur relèvent de votre organisme et de son OPCO. La plateforme fournit la
            trace, elle ne se prononce pas à leur place.
          </p>
        </div>
      )}

      {/* Indicateurs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <div key={i} className="soft-card p-4">
            <div className="text-[12px] text-[#8E8FA5] font-medium">{s.label}</div>
            <div className="text-[22px] font-bold mt-0.5"
                 style={{ color: s.color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {chargement ? '—' : s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Les formations préparées où personne n'est inscrit.
          Une feuille sans apprenant ne peut rien prouver : pas de signature,
          pas d'attestation, et aucun questionnaire ne peut partir — la tâche
          horaire s'y fait refuser « Aucun apprenant inscrit » et recommence
          chaque heure, sans que personne le voie.
          Et la clôture automatique ne les enterre plus : elles restent
          ouvertes, parce que « Terminée » sur une formation vide serait un
          faux muet, et « Annulée » affirmerait qu'elle n'a pas eu lieu — ce
          que la plateforme ne sait pas. C'est à vous de trancher, et ce
          bandeau est là pour que la question se pose. */}
      {!chargement && vides.length > 0 && (
        <div className="soft-card p-4 border-l-4" style={{ borderLeftColor: '#F59E0B' }}
             data-testid="emarg-sans-apprenant">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-[13.5px] font-semibold text-[#1A1B2E] flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-[#B45309]" />
                {vides.length} formation{vides.length > 1 ? 's' : ''} sans aucun apprenant
                {videsPassees.length > 0 && (
                  <span className="text-[#B45309]">
                    , dont {videsPassees.length} déjà passée{videsPassees.length > 1 ? 's' : ''}
                  </span>
                )}
                {videsImminentes.length > 0 && (
                  <span className="text-[#B91C1C]">
                    {' '}· {videsImminentes.length} démarre{videsImminentes.length > 1 ? 'nt' : ''} maintenant
                  </span>
                )}
              </div>
              <div className="text-[12px] text-[#6B6C85] mt-0.5">
                Ni émargement, ni attestation, ni questionnaire ne peuvent en sortir.
                Si elles ont eu lieu, il manque les inscriptions&nbsp;; sinon, annulez-les.
                La clôture automatique les laisse ouvertes plutôt que de les dire terminées.
              </div>
            </div>
            <button
              onClick={() => setSansApprenant((v) => !v)}
              data-testid="emarg-voir-sans-apprenant"
              className="shrink-0 h-9 px-4 rounded-lg text-[13px] font-semibold border"
              style={sansApprenant
                ? { borderColor: '#B45309', backgroundColor: '#FEF3C7', color: '#92400E' }
                : { borderColor: '#EFF0F6', color: '#4A4B63' }}
            >
              {sansApprenant ? 'Voir toutes les formations' : 'Les voir'}
            </button>
          </div>
        </div>
      )}

      {/* Tableau */}
      <div className="soft-card overflow-hidden">
        <div className="px-5 py-4 border-b border-[#EFF0F6] flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-[15.5px] font-semibold text-[#1A1B2E]"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Émargements par session
            </h3>
            <p className="text-[12.5px] text-[#8E8FA5]">Suivi des présences et signatures numériques</p>
          </div>
          <div className="flex items-center gap-1 bg-[#F3F4F8] rounded-lg p-0.5">
            {[['Tout', true], ['À compléter', false]].map(([libelle, valeur]) => (
              <button
                key={libelle}
                onClick={() => setTout(valeur)}
                data-testid={`filtre-${valeur ? 'tout' : 'a-completer'}`}
                className={`h-8 px-3 rounded-md text-[12.5px] font-semibold transition ${
                  tout === valeur ? 'bg-white text-[#1A1B2E] shadow-sm' : 'text-[#6B6C85]'
                }`}
              >
                {libelle}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5] border-b border-[#EFF0F6]">
                <th className="py-3.5 px-5">Session</th>
                <th className="py-3.5 px-5">Date</th>
                <th className="py-3.5 px-5">Présences</th>
                <th className="py-3.5 px-5">Signatures</th>
                <th className="py-3.5 px-5">Conformité</th>
                <th className="py-3.5 px-5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {chargement && (
                <tr><td colSpan={6} className="py-10 text-center text-[13px] text-[#8E8FA5]">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…
                </td></tr>
              )}
              {!chargement && feuilles.length === 0 && (
                <tr><td colSpan={6} className="py-12 text-center">
                  <div className="text-[13.5px] font-semibold text-[#1A1B2E]">Aucune feuille</div>
                  <div className="text-[12.5px] text-[#8E8FA5] mt-1">
                    Les feuilles se préparent seules sept jours avant chaque formation.
                  </div>
                </td></tr>
              )}
              {!chargement && (sansApprenant ? vides : feuilles).map((f) => (
                <Rangee
                  key={f.session_id}
                  f={f}
                  occupe={occupe}
                  onAgir={agir}
                  onDeposer={demanderFichier}
                  onVoirPieces={setPieces}
                  onAttestations={setAttestations}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Rangee({ f, occupe, onAgir, onDeposer, onVoirPieces, onAttestations }) {
  const total = f.attendues || 0;
  // La présence se compte sur les apprenants, son dénominateur aussi : la
  // colonne affichait « 2 / 4 » sur une formation où tout le monde avait
  // signé — deux présences d'apprenant sur quatre lignes, dont deux étaient
  // celles de la formatrice.
  const attendusApprenants = f.attendues_apprenants ?? total;
  const pctSignatures = total ? (f.signees / total) * 100 : 0;
  const enCours = (a) => occupe === `${f.session_id}:${a}`;
  // `complete` vaut vrai dès qu'une preuve existe — signée en ligne OU déposée
  // sur papier. La colonne doit dire laquelle : annoncer « 100 % conforme » sur
  // une feuille scannée surestime, et afficher « 40 % partiel » alors que le
  // papier est rangé sous-estime. On relit donc les deux séparément.
  const signeeEnLigne = total > 0 && f.signees >= total;

  return (
    <tr className="data-row border-b border-[#F3F4F8] last:border-0" data-testid={`feuille-${f.session_id}`}>
      <td className="py-4 px-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#EEF2FF] flex items-center justify-center shrink-0">
            <ClipboardCheck className="w-4 h-4 text-[#4F46E5]" />
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{f.titre || '—'}</div>
            <div className="text-[11px] text-[#8E8FA5] truncate">
              {[f.formateur, f.seances ? `${f.seances} séance${f.seances > 1 ? 's' : ''}` : null]
                .filter(Boolean).join(' · ') || f.session_id}
            </div>
            {/* Un lien mort ne se voyait nulle part : on l'envoyait, l'écran
                disait « envoyé », et le formateur tombait sur « Ce lien a
                expiré ». L'envoyer le prolonge désormais. */}
            {f.lien_expire && (
              <div className="text-[10.5px] font-semibold text-[#B45309] truncate">
                lien expiré le {dateFr(f.lien_expire_le)} — l'envoyer le prolonge
              </div>
            )}
          </div>
        </div>
      </td>

      <td className="py-4 px-5 text-[13px] text-[#4A4B63] whitespace-nowrap">
        {f.debut === f.fin ? dateFr(f.debut) : `${dateFr(f.debut)} → ${dateFr(f.fin)}`}
      </td>

      <td className="py-4 px-5">
        {/* Aucune ligne d'apprenant : « 0 / 0 » ferait chercher des absents qui
            n'existent pas. Sept feuilles sur dix étaient dans ce cas, et la
            colonne annonçait « 0 / 2 » — la ligne du formateur. */}
        {f.preparee && attendusApprenants === 0 ? (
          <span className="text-[11.5px] text-[#B45309] font-semibold"
                title="La feuille est préparée, mais personne n'y est inscrit">
            aucun apprenant
          </span>
        ) : f.preparee ? (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#0F766E]">
              <CheckCircle2 className="w-3.5 h-3.5" /> {f.presents}
            </span>
            {f.absents > 0 && (
              <span className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#B91C1C]">
                <XCircle className="w-3.5 h-3.5" /> {f.absents}
              </span>
            )}
            <span className="text-[11.5px] text-[#8E8FA5]">/ {attendusApprenants}</span>
          </div>
        ) : <span className="text-[12.5px] text-[#B8B9CC]">—</span>}
      </td>

      <td className="py-4 px-5">
        {f.preparee ? (
          <div className="flex items-center gap-2">
            <div className="flex-1 max-w-[100px] h-1.5 bg-[#F3F4F8] rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-[#14B8A6]" style={{ width: `${pctSignatures}%` }} />
            </div>
            <span className="text-[12px] font-semibold text-[#4A4B63] whitespace-nowrap">
              {f.signees}/{total}
            </span>
          </div>
        ) : <span className="text-[12.5px] text-[#B8B9CC]">—</span>}
      </td>

      <td className="py-4 px-5 whitespace-nowrap">
        {signeeEnLigne ? (
          <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2.5 py-1 rounded-md bg-[#E6FBF7] text-[#0F766E]">
            <CheckCircle2 className="w-3 h-3" /> 100% conforme
          </span>
        ) : f.feuilles_deposees > 0 ? (
          // Une feuille deposee est une preuve de realisation, meme si aucune
          // ligne n'est signee en ligne : la formation a eu lieu sur papier.
          <button
            onClick={() => onVoirPieces(f)}
            data-testid={`deposee-${f.session_id}`}
            title="Voir la feuille déposée"
            className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2.5 py-1 rounded-md bg-[#E6FBF7] text-[#0F766E] hover:bg-[#D3F7F0]"
          >
            <Paperclip className="w-3 h-3" />
            {f.feuilles_deposees > 1 ? `${f.feuilles_deposees} feuilles déposées` : 'Feuille déposée'}
          </button>
        ) : !f.preparee ? (
          <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2.5 py-1 rounded-md bg-[#FEF3E7] text-[#B45309]">
            <AlertTriangle className="w-3 h-3" /> Non préparée
          </span>
        ) : (
          <span className={`inline-flex items-center gap-1 text-[11.5px] font-semibold px-2.5 py-1 rounded-md ${
            f.passee ? 'bg-[#FEF6F6] text-[#B91C1C]' : 'bg-[#FEF3E7] text-[#B45309]'
          }`}>
            {Math.round(pctSignatures)}% partiel
          </span>
        )}
      </td>

      <td className="py-4 px-5">
        <div className="flex items-center gap-1">
          {!f.preparee ? (
            <button
              onClick={() => onAgir(f, 'preparer')} disabled={!!occupe}
              data-testid={`preparer-${f.session_id}`}
              className="h-8 px-3 rounded-lg bg-[#4F46E5] text-white text-[12px] font-semibold flex items-center gap-1.5 hover:opacity-95 disabled:opacity-60"
            >
              {enCours('preparer') ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                   : <ClipboardCheck className="w-3.5 h-3.5" />}
              Préparer
            </button>
          ) : (
            <>
              <BoutonIcone titre={f.lien_expire
                             ? "Renvoyer le lien au formateur — il repart valable 15 jours"
                             : "Envoyer le lien au formateur"}
                           testid={`envoyer-${f.session_id}`}
                           onClick={() => onAgir(f, 'envoyer')} occupe={!!occupe}
                           charge={enCours('envoyer')} Icone={Send} accent />
              <BoutonIcone titre="Copier le lien" testid={`lien-${f.session_id}`}
                           onClick={() => onAgir(f, 'lien')} occupe={!!occupe}
                           charge={enCours('lien')} Icone={Link2} accent />
              <BoutonIcone titre="Ouvrir la feuille" testid={`pdf-${f.session_id}`}
                           onClick={() => onAgir(f, 'pdf')} occupe={!!occupe}
                           charge={enCours('pdf')} Icone={Download} />
            </>
          )}
          {/* Le depot vaut pour toutes les lignes, preparees ou non : une
              formation passee dont la feuille n'a jamais ete creee est
              justement celle dont on n'a qu'un papier signe. */}
          <BoutonIcone titre="Déposer la feuille signée (scan ou photo)"
                       testid={`deposer-${f.session_id}`}
                       onClick={() => onDeposer(f)} occupe={!!occupe}
                       charge={enCours('deposer')} Icone={Upload} />
          {f.feuilles_deposees > 0 && (
            <BoutonIcone titre={`${f.feuilles_deposees} feuille(s) déposée(s)`}
                         testid={`pieces-${f.session_id}`}
                         onClick={() => onVoirPieces(f)} occupe={false}
                         charge={false} Icone={Paperclip} accent />
          )}
          {/* L'attestation suit la feuille : c'est l'assiduité qu'elle
              certifie, et c'est ici qu'on la lit. */}
          <BoutonIcone titre="Attestations de fin de formation"
                       testid={`attestations-${f.session_id}`}
                       onClick={() => onAttestations(f)} occupe={false}
                       charge={false} Icone={Award} accent />
        </div>
      </td>
    </tr>
  );
}

// À qui l'on envoie, et ce qu'on envoie.
//
// Deux chemins existent et ils ne s'excluent pas : **le lien** pour signer au
// doigt sur le téléphone, et **la feuille** pour l'imprimer et la faire signer
// à la main quand la salle n'a pas de réseau — le papier signé se dépose
// ensuite ici, et vaut preuve de réalisation.
//
// L'adresse est celle de la fiche du formateur quand il en a une. Quand aucune
// fiche ne porte son nom — « BRUNET ELODIE » alors que la fiche s'appelle
// « BRUNET » —, l'envoi se faisait refuser en invitant à saisir une adresse que
// l'écran ne proposait pas. Elle se saisit ici.
function PanneauEnvoi({ feuille, onFermer, onEnvoye }) {
  const [adresse, setAdresse] = useState(feuille.formateur_email || '');
  const [avecLien, setAvecLien] = useState(true);
  const [avecFeuille, setAvecFeuille] = useState(false);
  const [occupe, setOccupe] = useState(false);

  const envoyer = async () => {
    setOccupe(true);
    try {
      const { data } = await api.post(`/sessions/${feuille.session_id}/emargement/envoyer`, {
        email: adresse.trim() || null,
        avec_lien: avecLien,
        avec_feuille: avecFeuille,
      });
      toast({
        title: data.avec_lien && data.avec_feuille ? 'Lien et feuille envoyés'
             : data.avec_lien ? 'Lien envoyé' : 'Feuille envoyée',
        description: `À ${data.destinataire}.`
          + (data.expire_le ? ` Lien valable jusqu'au ${dateFr(data.expire_le)}.` : ''),
      });
      onEnvoye && onEnvoye();
      onFermer();
    } catch (err) {
      toast({ title: 'Envoi impossible', description: messageErreur(err),
              variant: 'destructive' });
    } finally { setOccupe(false); }
  };

  return (
    <div className="soft-card p-5" data-testid={`panneau-envoi-${feuille.session_id}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#EEF2FF] flex items-center justify-center shrink-0">
          <Send className="w-4 h-4 text-[#4F46E5]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold text-[#1A1B2E]"
               style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Envoyer l'émargement
          </div>
          <div className="text-[12px] text-[#8E8FA5] truncate">
            {feuille.titre || feuille.session_id}
            {feuille.formateur ? ` · ${feuille.formateur}` : ''}
          </div>
        </div>
        <button onClick={onFermer} data-testid="fermer-envoi"
                title="Fermer" aria-label="Fermer"
                className="w-8 h-8 rounded-lg hover:bg-[#F6F7FB] text-[#6B6C85] flex items-center justify-center">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="mt-4 space-y-3">
        <label className="block">
          <span className="text-[12px] font-semibold text-[#4A4B63]">Destinataire</span>
          <input
            value={adresse} onChange={(e) => setAdresse(e.target.value)}
            type="email" placeholder="prenom.nom@exemple.fr"
            data-testid="envoi-adresse"
            className="mt-1 w-full h-9 px-3 rounded-lg border border-[#EFF0F6] text-[13px]"
          />
          <span className="text-[11px] text-[#8E8FA5]">
            {feuille.formateur_email
              ? "L'adresse de la fiche du formateur — corrigez-la pour envoyer ailleurs."
              : `Aucune adresse sur la fiche de « ${feuille.formateur || 'ce formateur'} » : saisissez-en une.`}
          </span>
        </label>

        <div className="space-y-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={avecLien} data-testid="envoi-avec-lien"
                   onChange={(e) => setAvecLien(e.target.checked)}
                   className="mt-0.5 accent-[#4F46E5]" />
            <span className="text-[12.5px] text-[#1A1B2E]">
              <strong>Le lien à signer à l'écran</strong>
              <span className="block text-[11px] text-[#8E8FA5]">
                Le formateur l'ouvre sur son téléphone et fait signer au doigt.
                L'envoyer prolonge le lien de quinze jours.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={avecFeuille} data-testid="envoi-avec-feuille"
                   onChange={(e) => setAvecFeuille(e.target.checked)}
                   className="mt-0.5 accent-[#4F46E5]" />
            <span className="text-[12.5px] text-[#1A1B2E]">
              <strong>La feuille à imprimer et faire signer à la main</strong>
              <span className="block text-[11px] text-[#8E8FA5]">
                Le PDF part en pièce jointe. Le papier signé se dépose ensuite
                sur la formation, et vaut preuve de réalisation.
              </span>
            </span>
          </label>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={envoyer}
            disabled={occupe || !adresse.trim() || (!avecLien && !avecFeuille)}
            data-testid="envoi-valider"
            title={!adresse.trim() ? 'Saisissez une adresse'
                 : (!avecLien && !avecFeuille) ? "Rien à envoyer : cochez le lien, la feuille, ou les deux"
                 : undefined}
            className="h-9 px-4 rounded-lg bg-[#4F46E5] text-white text-[13px] font-semibold flex items-center gap-1.5 hover:opacity-95 disabled:opacity-50"
          >
            {occupe ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : avecFeuille && !avecLien ? <Printer className="w-3.5 h-3.5" />
                    : <Send className="w-3.5 h-3.5" />}
            Envoyer
          </button>
          <button onClick={onFermer} disabled={occupe}
                  className="h-9 px-4 rounded-lg bg-white border border-[#EFF0F6] text-[13px] font-semibold text-[#6B6C85] hover:bg-[#F6F7FB]">
            Annuler
          </button>
        </div>

        <ApprenantsASigner sessionId={feuille.session_id} onEnvoye={onEnvoye} />
      </div>
    </div>
  );
}

// Aux apprenants : chacun signe sa propre ligne.
//
// Le lien de la formation est au formateur et montre toutes les lignes — c'est
// juste quand son téléphone circule et que chacun signe devant lui. Dès qu'il
// arrive dans une boîte, ça cesse de l'être : une apprenante a reçu la feuille
// entière, y a vu la ligne de sa formatrice, et aurait pu la signer.
//
// Ici, un lien par personne, envoyé **après** que le formateur a validé la
// présence : il ne porte que ses lignes validées, donc il ne signe ni pour
// autrui, ni une séance où il n'était pas. Et il se renvoie — c'est ce qui
// permet de le faire vivre jusqu'à la fin du lendemain seulement.
function ApprenantsASigner({ sessionId, onEnvoye }) {
  const [etat, setEtat] = useState(null);
  const [occupe, setOccupe] = useState(null);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get(`/sessions/${sessionId}/emargement/apprenants`);
      setEtat(data.apprenants || []);
    } catch {
      setEtat([]);
    }
  }, [sessionId]);

  useEffect(() => { charger(); }, [charger]);

  const envoyer = async (ids, cle) => {
    setOccupe(cle);
    try {
      const { data } = await api.post(
        `/sessions/${sessionId}/emargement/apprenants/envoyer`,
        { participant_ids: ids },
      );
      const n = (data.envoyes || []).length;
      const sans = (data.sans_adresse || []).length;
      const ko = (data.echecs || []).length;
      toast({
        title: n ? `Lien envoyé à ${n} apprenant${n > 1 ? 's' : ''}` : 'Aucun envoi',
        description: [
          sans ? `${sans} sans adresse` : null,
          ko ? `${ko} en échec` : null,
        ].filter(Boolean).join(' · ')
          || 'Chacun ne peut signer que sa propre ligne.',
        variant: n ? undefined : 'destructive',
      });
      await charger();
      onEnvoye && onEnvoye();
    } catch (err) {
      toast({ title: 'Envoi impossible', description: messageErreur(err),
              variant: 'destructive' });
    } finally { setOccupe(null); }
  };

  if (!etat) return null;
  if (!etat.length) {
    return (
      <div className="mt-4 pt-4 border-t border-[#EFF0F6] text-[12px] text-[#8E8FA5]">
        Aucune ligne d&rsquo;apprenant sur cette feuille.
      </div>
    );
  }

  const aRelancer = etat.filter((a) => a.a_signer > 0 && a.email);
  const sansAdresse = etat.filter((a) => a.a_signer > 0 && !a.email);

  return (
    <div className="mt-4 pt-4 border-t border-[#EFF0F6]">
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0">
          <div className="text-[12.5px] font-semibold text-[#1A1B2E]">
            Aux apprenants, pour qu&rsquo;ils signent eux-mêmes
          </div>
          <div className="text-[11px] text-[#8E8FA5]">
            Chacun reçoit un lien qui ne porte que son émargement, et seulement
            les séances où vous avez validé sa présence. Il vaut jusqu&rsquo;à la fin
            de la journée de demain, et se renvoie.
          </div>
        </div>
        {aRelancer.length > 0 && (
          <button
            onClick={() => envoyer(null, 'tous')}
            disabled={!!occupe}
            data-testid="envoi-apprenants-tous"
            className="shrink-0 h-9 px-3 rounded-lg bg-[#4F46E5] text-white text-[12.5px] font-semibold flex items-center gap-1.5 hover:opacity-95 disabled:opacity-50"
          >
            {occupe === 'tous' ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                               : <Send className="w-3.5 h-3.5" />}
            Envoyer aux {aRelancer.length}
          </button>
        )}
      </div>

      {sansAdresse.length > 0 && (
        <div className="text-[11px] font-semibold text-[#B45309] mb-1.5">
          {sansAdresse.length} apprenant{sansAdresse.length > 1 ? 's' : ''} sans
          adresse : {sansAdresse.map((a) => a.nom).join(', ')} — renseignez-la sur
          leur fiche.
        </div>
      )}

      <div className="rounded-lg border border-[#EFF0F6] divide-y divide-[#F3F4F8]">
        {etat.map((a) => (
          <div key={a.participant_id} className="px-2.5 py-2 flex items-center gap-2"
               data-testid={`apprenant-${a.participant_id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">
                {a.nom}
              </div>
              <div className="text-[11px] text-[#8E8FA5] truncate">
                {a.validees} validée(s) · {a.signees} signée(s)
                {a.email ? ` · ${a.email}` : ''}
                {a.envoye_le ? ` · envoyé le ${dateHeureFr(a.envoye_le)}` : ''}
                {a.envois > 1 ? ` (${a.envois} fois)` : ''}
              </div>
              {!a.email && (
                <div className="text-[10.5px] font-semibold text-[#B45309]">
                  aucune adresse sur sa fiche
                </div>
              )}
            </div>
            {a.a_signer === 0 ? (
              <span className="shrink-0 text-[10.5px] font-semibold px-2 py-0.5 rounded-md bg-[#E6FBF7] text-[#0F766E]">
                {a.validees ? 'tout est signé' : 'rien à signer'}
              </span>
            ) : (
              <button
                onClick={() => envoyer([a.participant_id], a.participant_id)}
                disabled={!!occupe || !a.email}
                data-testid={`envoyer-apprenant-${a.participant_id}`}
                title={!a.email ? 'Renseignez son adresse sur sa fiche'
                     : a.envois ? 'Renvoyer son lien et le prolonger'
                     : 'Lui envoyer son lien'}
                className="shrink-0 h-8 px-2.5 rounded-lg bg-white border border-[#C7D2FE] text-[12px] font-semibold text-[#4F46E5] flex items-center gap-1.5 hover:bg-[#EEF2FF] disabled:opacity-50"
              >
                {occupe === a.participant_id
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Send className="w-3.5 h-3.5" />}
                {a.envois ? 'Renvoyer' : 'Envoyer'}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// Les pieces deposees sur une formation : les ouvrir, en retirer une.
//
// Le retrait efface pour de bon — on scanne la mauvaise page, ca arrive — mais
// le depot comme le retrait sont au journal d'audit : une preuve de
// realisation qui disparait sans qu'on sache qui l'a retiree ne vaut pas mieux
// que pas de preuve.
function PanneauPieces({ feuille, onFermer, onChange }) {
  const [liste, setListe] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [occupe, setOccupe] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get(`/sessions/${feuille.session_id}/documents`, {
        params: { genre: 'emargement' },
      });
      setListe(data);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || 'Impossible de lire les pièces.',
        variant: 'destructive',
      });
    } finally { setChargement(false); }
  }, [feuille.session_id]);

  useEffect(() => { charger(); }, [charger]);

  const ouvrir = async (p) => {
    setOccupe(p.id);
    try {
      const res = await api.get(`/sessions/${feuille.session_id}/documents/${p.id}`,
                                { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast({ title: 'Impossible', description: 'La pièce ne s\'ouvre pas.', variant: 'destructive' });
    } finally { setOccupe(null); }
  };

  const retirer = async (p) => {
    if (!window.confirm(`Retirer « ${p.filename} » ? La preuve de réalisation ne sera plus là.`)) return;
    setOccupe(p.id);
    try {
      await api.delete(`/sessions/${feuille.session_id}/documents/${p.id}`);
      toast({ title: 'Pièce retirée', description: p.filename });
      await charger();
      onChange && onChange();
    } catch (err) {
      toast({
        title: 'Impossible',
        description: err?.response?.data?.detail || 'Retrait refusé.',
        variant: 'destructive',
      });
    } finally { setOccupe(null); }
  };

  return (
    <div className="soft-card p-5" data-testid={`panneau-pieces-${feuille.session_id}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#E6FBF7] flex items-center justify-center shrink-0">
          <Paperclip className="w-4 h-4 text-[#0F766E]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold text-[#1A1B2E]"
               style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Feuilles déposées
          </div>
          <div className="text-[12px] text-[#8E8FA5] truncate">{feuille.titre || feuille.session_id}</div>
        </div>
        <button onClick={onFermer} data-testid="fermer-pieces"
                title="Fermer" aria-label="Fermer"
                className="w-8 h-8 rounded-lg hover:bg-[#F6F7FB] text-[#6B6C85] flex items-center justify-center">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="mt-3 divide-y divide-[#F3F4F8]">
        {chargement && (
          <div className="py-6 text-center text-[13px] text-[#8E8FA5]">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…
          </div>
        )}
        {!chargement && liste.length === 0 && (
          <div className="py-6 text-center text-[13px] text-[#8E8FA5]">
            Aucune pièce déposée sur cette formation.
          </div>
        )}
        {!chargement && liste.map((p) => (
          <div key={p.id} className="py-2.5 flex items-center gap-2" data-testid={`piece-${p.id}`}>
            <div className="flex-1 min-w-0">
              <button onClick={() => ouvrir(p)} disabled={!!occupe}
                      data-testid={`ouvrir-piece-${p.id}`}
                      className="text-[13px] font-semibold text-[#4F46E5] hover:underline truncate block max-w-full text-left disabled:opacity-60">
                {p.filename}
              </button>
              <div className="text-[11px] text-[#8E8FA5]">
                {[
                  p.size ? `${Math.max(1, Math.round(p.size / 1024))} Ko` : null,
                  p.depose_le ? `déposée le ${new Date(p.depose_le).toLocaleDateString('fr-FR')}` : null,
                  p.depose_par || null,
                ].filter(Boolean).join(' · ')}
              </div>
            </div>
            <button onClick={() => retirer(p)} disabled={!!occupe}
                    data-testid={`retirer-piece-${p.id}`}
                    title="Retirer cette pièce" aria-label="Retirer cette pièce"
                    className="w-8 h-8 rounded-lg hover:bg-[#FEF6F6] text-[#B91C1C] flex items-center justify-center disabled:opacity-50">
              {occupe === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                               : <Trash2 className="w-3.5 h-3.5" />}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


function BoutonIcone({ titre, testid, onClick, occupe, charge, Icone, accent }) {
  return (
    <button
      title={titre} aria-label={titre} data-testid={testid}
      onClick={onClick} disabled={occupe}
      className={`w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-50 ${
        accent ? 'hover:bg-[#EEF2FF] text-[#4F46E5]' : 'hover:bg-[#F6F7FB] text-[#6B6C85]'
      }`}
    >
      {charge ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icone className="w-3.5 h-3.5" />}
    </button>
  );
}
